/**
 * The approvals section's reads (owner, 17 Oct 2026: "Create a dedicated
 * approval section for surbhi for all the approvals coming in and for
 * everybody").
 *
 * Every read pins `book: null`: the server works out each person's books
 * itself, so the counts do not change when the header flips book. Each row
 * says which book to answer it from, and the answer goes to that kind's own
 * endpoint with that book.
 */
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { hasApprovalsSection, type ApprovalAnswered, type ApprovalKind, type ApprovalsSummary, type CompanyExpenseRow, type DealerOnboardingRow, type LeaveView, type ShowroomCheckRow, type VehicleReturnRow, type VehicleTransferRow } from '@bright/shared';

import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { BookKey } from '@/lib/book';
import type { ExpenseRow } from '@/features/field/field';
import type { OrderRow } from '@/features/orders/OrderDrawer';

export interface CounterBalance {
  remaining: number;
  overdue: number;
  missing: number;
}

export type ApprovalOrderRow = OrderRow & {
  /** This person answers it: as the counter's ASM, as the office when no ASM can, or on the balance check. */
  mayAnswer: boolean;
  /** Which of those (23 Oct 2026) — the balance check has its own three answers. */
  answerAs: 'asm' | 'office' | 'balance' | null;
  /** Who else it waits for, in words: the ASM, the balance check — never this person's own side. */
  waitingFor: string | null;
  answerBook: BookKey | null;
};

export interface ApprovalsBoard {
  kinds: ApprovalKind[];
  /** Dealers the team has brought to Sunny (27 Oct 2026). */
  dealers?: DealerOnboardingRow[];
  /** Company expenses (31 Oct 2026): Sunny's over ₹50,000, the office's, the pending screenshots and bills, the record. */
  companyExpenses?: { sunny: CompanyExpenseRow[]; approval: CompanyExpenseRow[]; pending: CompanyExpenseRow[]; done: CompanyExpenseRow[] };
  /** Returns (30 Oct 2026): Surbhi's photo check on a counter's own return (31 Oct), Rahul's pile, then Neeraj's. */
  returns?: { photos: VehicleReturnRow[]; dispatch: VehicleReturnRow[]; bill: VehicleReturnRow[] };
  transfers?: { pending: VehicleTransferRow[]; receiver: VehicleTransferRow[] };
  orders?: { rows: ApprovalOrderRow[]; balances: Record<string, CounterBalance> };
  leave?: Array<LeaveView & { bookKey: BookKey | null }>;
  expenses?: Array<ExpenseRow & { book: string; bookKey: BookKey | null; answerBook: BookKey | null }>;
  /** Mitras' and outlets' showroom checks (23 Oct 2026): the ASM's own counters', or every one in the office's books. */
  showrooms?: Array<ShowroomCheckRow & { answerBook: BookKey | null }>;
  enrolments?: Array<{
    id: string;
    name: string;
    username: string | null;
    role: string;
    asm: string | null;
    counter: string | null;
    enrolledBy: string | null;
    book: string;
    bookKey: BookKey | null;
    answerBook: BookKey | null;
    createdAt: string;
  }>;
  verifications?: Array<{ userId: string; name: string; role: string; book: string; bookKey: BookKey | null; answerBook: BookKey | null; submittedAt: string }>;
  tasks?: { waiting: number };
}

export interface ApprovalsAsked {
  leave?: LeaveView[];
  expenses?: ExpenseRow[];
  orders?: OrderRow[];
  enrolments?: Array<{ id: string; name: string; role: string; enrolment: 'PENDING' | 'APPROVED' | 'REJECTED'; note: string | null; reviewedBy: string | null; at: string }>;
  verification?: { status: 'SUBMITTED' | 'VERIFIED' | 'REJECTED'; submittedAt: string | null; reviewedBy: string | null; reviewedAt: string | null; note: string | null } | null;
}

export const approvalKeys = {
  all: ['approvals'] as const,
  summary: ['approvals', 'summary'] as const,
  board: ['approvals', 'board'] as const,
  asked: ['approvals', 'asked'] as const,
  answered: ['approvals', 'answered'] as const,
};

/** Whether this sign-in has the section at all — every one but a counter's. */
export function useHasApprovals(): boolean {
  const { user } = useAuth();
  return hasApprovalsSection(user);
}

/** The counts — the rail and the reminder bar, on every page. */
export function useApprovalsSummary() {
  const enabled = useHasApprovals();
  return useQuery({
    queryKey: approvalKeys.summary,
    queryFn: () => api.get<ApprovalsSummary>('/approvals/summary', undefined, { book: null }),
    enabled,
    staleTime: 60_000,
    refetchInterval: 2 * 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useApprovalsBoard(enabled = true) {
  return useQuery({
    queryKey: approvalKeys.board,
    queryFn: () => api.get<ApprovalsBoard>('/approvals', undefined, { book: null }),
    enabled,
    // Every 20 seconds (31 Oct 2026, "this system has to be fast and real time").
    refetchInterval: 20_000,
    refetchOnWindowFocus: true,
  });
}

/** What this person answered, newest first (5 Nov 2026). */
export function useApprovalsAnswered(enabled = true) {
  return useQuery({
    queryKey: approvalKeys.answered,
    queryFn: () => api.get<ApprovalAnswered[]>('/approvals/answered', undefined, { book: null }),
    enabled,
  });
}

export function useApprovalsAsked(enabled = true) {
  return useQuery({
    queryKey: approvalKeys.asked,
    queryFn: () => api.get<ApprovalsAsked>('/approvals/mine', undefined, { book: null }),
    enabled,
    refetchOnWindowFocus: true,
  });
}

/**
 * The section's counts and lists, read afresh — for every page that answers
 * an approval of its own (transfers, leave, expenses, enrolments, orders,
 * Aadhaar checks), so the rail and the bar clear the moment it is answered
 * there, as each kind's old bar did (review, 17 Oct 2026).
 */
export function refreshApprovals(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: approvalKeys.all });
}

/** After an answer: the section, and the page each kind already had, read afresh. */
export function refreshAfterAnswer(queryClient: QueryClient, kind: ApprovalKind): void {
  void queryClient.invalidateQueries({ queryKey: approvalKeys.all });
  const own: Record<ApprovalKind, ReadonlyArray<readonly string[]>> = {
    companyExpenses: [['company-expenses']],
    returns: [['returns'], ['vehicles'], ['vehicle'], ['dispatches'], ['store']],
    dealers: [['dealer-onboarding']],
    transfers: [['transfers']],
    orders: [['orders'], ['my-requests'], ['ledger', 'payment-pending']],
    leave: [['attendance']],
    expenses: [['field']],
    showrooms: [['showrooms']],
    enrolments: [['admin', 'enrolments', 'pending'], ['admin', 'users']],
    verifications: [['admin', 'users']],
    tasks: [['tasks']],
  };
  for (const key of own[kind]) void queryClient.invalidateQueries({ queryKey: key as readonly unknown[] });
}

/** Takes an answered row off the board at once, so a second tap cannot answer it again while the board reloads. */
export function dropFromBoard(queryClient: QueryClient, kind: ApprovalKind, id: string): void {
  queryClient.setQueryData<ApprovalsBoard>(approvalKeys.board, (old) => {
    if (!old) return old;
    const next: ApprovalsBoard = { ...old };
    if (kind === 'companyExpenses' && old.companyExpenses) {
      const keep = (rows: CompanyExpenseRow[]) => rows.filter((row) => row.id !== id);
      next.companyExpenses = { ...old.companyExpenses, sunny: keep(old.companyExpenses.sunny), approval: keep(old.companyExpenses.approval) };
    } else if (kind === 'returns' && old.returns) {
      const keep = (rows: VehicleReturnRow[]) => rows.filter((row) => row.id !== id);
      next.returns = { photos: keep(old.returns.photos), dispatch: keep(old.returns.dispatch), bill: keep(old.returns.bill) };
    } else if (kind === 'dealers' && old.dealers) {
      next.dealers = old.dealers.filter((row) => row.id !== id);
    } else if (kind === 'transfers' && old.transfers) {
      next.transfers = { pending: old.transfers.pending.filter((row) => row.id !== id), receiver: old.transfers.receiver.filter((row) => row.id !== id) };
    } else if (kind === 'orders' && old.orders) {
      next.orders = { ...old.orders, rows: old.orders.rows.filter((row) => row.id !== id) };
    } else if (kind === 'leave' && old.leave) {
      next.leave = old.leave.filter((row) => row.id !== id);
    } else if (kind === 'expenses' && old.expenses) {
      next.expenses = old.expenses.filter((row) => row.id !== id);
    } else if (kind === 'showrooms' && old.showrooms) {
      next.showrooms = old.showrooms.filter((row) => row.id !== id);
    } else if (kind === 'enrolments' && old.enrolments) {
      next.enrolments = old.enrolments.filter((row) => row.id !== id);
    } else if (kind === 'verifications' && old.verifications) {
      next.verifications = old.verifications.filter((row) => row.userId !== id);
    }
    return next;
  });
}
